import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { useParams, useNavigate } from "react-router-dom";

import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import PageAPI from "../../api/PageAPI";
import { newToastEvent } from "../../../utils/ToastUtil";
import { addPageToPages } from "../../../utils/PageUtils";

const useAddPage = () => {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantID }: any = useParams();
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();

  const addPage = useMutation({
    mutationFn: async (params: {
      payload: any;
      onSuccess?: () => void;
      onError?: () => void;
    }) => {
      const { payload, onSuccess } = params;

      if (
        !wPermissions.workspace_create_page &&
        !cPermissions.cloud_create_page
      ) {
        return Promise.reject(
          "You don't have permission to create pages in this workspace"
        );
      }

      let data = await PageAPI.createPage(workspaceID, payload);
      let {
        page: newPage,
        user: updatedUser,
        workspace: updatedWorkspace,
      } = data.data.data;

      return { newPage, updatedUser, updatedWorkspace, onSuccess };
    },

    onSuccess: ({ newPage, updatedUser, updatedWorkspace, onSuccess }) => {
      queryClient.setQueryData(["user", tenantID], (prev) => ({
        ...(prev || {}),
        user: updatedUser,
      }));
      queryClient.setQueryData(["workspace", workspaceID], (data) => ({
        ...(data || {}),
        workspace: updatedWorkspace,
      }));
      queryClient.invalidateQueries({
        queryKey: ["workspaces", tenantID],
      });

      addPageToPages(queryClient, newPage, workspaceID);

      newToastEvent(
        "success",
        `Successfully created your ${newPage.Name} page.`
      );

      const newLink = `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${newPage.id}/views/None`;
      navigate(newLink);
      callFunctionIfExists(onSuccess);
    },
    onError: (err, { onError }) => {
      callFunctionIfExists(onError, getErrorMessage(err));
    },
  });

  return {
    addPage: addPage.mutate,
    addSubmitting: addPage.isPending,
    addError: addPage.isError,
  };
};

export default useAddPage;
